import { PageSize } from '@/constant';
import {
	GetWorkerChangeWorkPlaceListParams,
	IWorkerChangeWorkPlace,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import persistReducer from 'redux-persist/es/persistReducer';

import { formatWorkerChangeWorkPlaceList, RootState } from '..';
import { storage } from '../store.persist';
import {
	IPayloadCreateWorkerChangeWorkPlaceRequest,
	IPayloadDeleteWorkerChangeWorkPlaceRequest,
	IPayloadGetWorkerChangeWorkPlaceDetailRequest,
	IPayloadGetWorkerChangeWorkPlaceDetailSuccess,
	IPayloadGetWorkerChangeWorkPlaceExcelListRequest,
	IPayloadGetWorkerChangeWorkPlaceListRequest,
	IPayloadGetWorkerChangeWorkPlaceListSuccess,
	IWorkerChangeWorkPlaceDetailStore,
	IWorkerChangeWorkPlaceListDetailStore,
} from './worker-change-work-place.type';

const InitParams: GetWorkerChangeWorkPlaceListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerChangeWorkPlaceList: [] as IWorkerChangeWorkPlaceListDetailStore[],
	total: 0,
	params: InitParams,
	workerChangeWorkPlaceDetail: {} as IWorkerChangeWorkPlaceDetailStore,
};

const workerChangeWorkPlaceSlice = createSlice({
	initialState,
	name: 'workerChangeWorkPlace',
	reducers: {
		/**---- GET WORKER CHANGE PLACE LIST START ---- */
		getWorkerChangeWorkPlaceListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerChangeWorkPlaceListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerChangeWorkPlaceListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerChangeWorkPlaceListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerChangeWorkPlaceList = content;
			state.total = total;
		},
		getWorkerChangeWorkPlaceListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerChangeWorkPlaceList = [];
		},

		getWorkerChangeWorkPlaceExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerChangeWorkPlaceExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerChangeWorkPlaceExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerChangeWorkPlaceExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER CHANGE PLACE LIST END ---- */

		/** UPDATE */
		updateWorkerChangeWorkPlaceSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<any>>
		) => {
			const { data } = action.payload;
			const workerChangeWorkPlaceList = [...state.workerChangeWorkPlaceList];
			state.workerChangeWorkPlaceList = formatWorkerChangeWorkPlaceList(
				mergeListItemUpdated(workerChangeWorkPlaceList, data)
			);
		},

		/**---- DELETE WORKER CHANGE PLACE START ---- */
		deleteWorkerChangeWorkPlaceRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerChangeWorkPlaceRequest>
		) => {
			state.fetching = true;
		},

		deleteWorkerChangeWorkPlaceSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerChangeWorkPlaceFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER CHANGE PLACE START ---- */

		/**---- GET WORKER CHANGE PLACE DETAIL START ---- */
		getWorkerChangeWorkPlaceDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerChangeWorkPlaceDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerChangeWorkPlaceDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerChangeWorkPlaceDetailSuccess>
		) => {
			const { workerChangeWorkPlaceDetail } = action.payload;
			state.fetching = false;
			state.workerChangeWorkPlaceDetail = workerChangeWorkPlaceDetail;
		},
		getWorkerChangeWorkPlaceDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER CHANGE PLACE DETAIL END ---- */

		/**---- GET SYSTEM NOTICE DETAIL START ---- */
		updateWorkerChangeWorkPlaceDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerChangeWorkPlace>>
		) => {
			const { data } = action.payload;
			const workerChangeWorkPlaceDetail = {
				...state.workerChangeWorkPlaceDetail,
			};
			state.workerChangeWorkPlaceDetail = mergeItemUpdated(
				workerChangeWorkPlaceDetail,
				data
			);
		},
		/**---- GET SYSTEM NOTICE DETAIL END ---- */

		/**---- CREATE WORKER CHANGE PLACE ---- */
		createWorkerChangeWorkPlaceRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerChangeWorkPlaceRequest>
		) => {
			state.fetching = true;
		},
		createWorkerChangeWorkPlaceSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerChangeWorkPlaceFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER CHANGE PLACE END ---- */
	},
});

export const WorkerChangeWorkPlaceSelectors = {
	selectFetching: (state: RootState) => state.workerChangeWorkPlace.fetching,
	selectParams: (state: RootState) => state.workerChangeWorkPlace.params,
	selectList: (state: RootState) =>
		state.workerChangeWorkPlace.workerChangeWorkPlaceList,
	selectTotal: (state: RootState) => state.workerChangeWorkPlace.total,
	selectDetail: (state: RootState) =>
		state.workerChangeWorkPlace.workerChangeWorkPlaceDetail,
};

export const WorkerChangeWorkPlaceActions = workerChangeWorkPlaceSlice.actions;

export const WORKER_CHANGE_WORK_PLACE_PERSIST_KEY =
	'workerChangeWorkPlacePersist';
export const WorkerChangeWorkPlaceReducer = persistReducer(
	{
		key: WORKER_CHANGE_WORK_PLACE_PERSIST_KEY,
		storage: storage,
		whitelist: ['workerChangeWorkPlaceDetail'],
	},
	workerChangeWorkPlaceSlice.reducer
);
